import React, { useState } from "react";
import { Link, Redirect } from "react-router-dom";
import PropTypes from "prop-types";
import styled from "styled-components";
import { InputAdornment, IconButton, Typography } from "@mui/material";
import { Info } from "@mui/icons-material";

import { A, P, H1, H3 } from "commons";
import { useInfoPopover, InfoPopover } from "commons/src/InfoPopover";
import { USERNAME_INPUT_INFO } from "commons/src/constants/messageConstants";

import { routes } from "../../helpers/RouteHelpers";
import { GlobalMsg, GoLogo, LiveChat, Support } from "../../components";
import { ClientSupportLink } from "./PlatformLinks";
import {
    AuthBlock,
    AuthButton,
    AuthTextInput,
    AuthContentHolder,
    ButtonLoading,
    Form,
    ShowPasswordButton
} from "./AuthComponents";

const usernameInfo = {
    header: <Typography>Username</Typography>,
    body: <Typography variant="body2">{USERNAME_INPUT_INFO}</Typography>
};

export function Login({
    clientId,
    adviserId,
    location,
    authenticate,
    registrationUser,
    termsAccepted,
    isLoading,
    failedLogIn,
    isPrintMode
}) {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [anchorElement, popoverContent, openInfoPopover, closeInfoPopover] = useInfoPopover();

    const login = e => {
        e.preventDefault();
        if (username && password) {
            authenticate(username, password, resetForm);
        }
    };

    const resetForm = () => {
        setUsername("");
        setPassword("");
    };

    if (adviserId && !isPrintMode) {
        return <Redirect to={routes.adviser.root()} />;
    }

    if (clientId) {
        if (termsAccepted) {
            return (
                <Redirect
                    to={
                        location?.state?.nextPathname
                            ? location.state.nextPathname
                            : routes.client.root()
                    }
                />
            );
        }
        return <Redirect to={routes.public.terms()} />;
    }

    return (
        <AuthContentHolder>
            <GlobalMsg />
            <AuthBlock>
                <GoLogo />
                <H1 secondary>Login</H1>
                {registrationUser && (
                    <WelcomeText data-testid="welcome-text">
                        <P>Thank you for registering using this service</P>

                        <P>
                            You can now access this service with the username {registrationUser} and
                            the password you have just provided
                        </P>
                    </WelcomeText>
                )}
                <Form onSubmit={login}>
                    <InfoPopover
                        handleClose={closeInfoPopover}
                        anchorEl={anchorElement}
                        content={popoverContent}
                        horizontalPosition="right"
                        componentStyles={{ contentContainer: { padding: "3px 12px 12px 12px" } }}
                    />
                    <AuthTextInput
                        data-testid="login-form-username"
                        value={username}
                        label="Username"
                        type="text"
                        onChange={e => setUsername(e.target.value)}
                        autoComplete="username email"
                        autoCapitalize="none"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="Username additional information"
                                        onClick={e => openInfoPopover(e, usernameInfo)}
                                        edge="end"
                                        size="large"
                                    >
                                        <Info />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <AuthTextInput
                        data-testid="login-form-password"
                        value={password}
                        type={isPasswordVisible ? "text" : "password"}
                        label="Password"
                        autoComplete="current-password"
                        onChange={e => setPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <ShowPasswordButton
                                    handleClickShowPassword={() => setIsPasswordVisible(v => !v)}
                                    isPasswordVisible={isPasswordVisible}
                                />
                            )
                        }}
                    />

                    <ButtonLoading displayWhen={!isLoading}>
                        <AuthButton data-testid="submit-button">Log in</AuthButton>
                    </ButtonLoading>
                </Form>

                <LoginH3 textAlign="center" secondary>
                    <Link to={routes.public.forgotten()}>Forgot your password?</Link>
                </LoginH3>
                <LoginH3 textAlign="center" secondary>
                    <Link to={routes.public.register()}>New user? Register here</Link>
                </LoginH3>
                <LoginH3 textAlign="center" secondary>
                    <A
                        href={ClientSupportLink}
                        rel="noopener noreferrer"
                        target="_blank"
                        data-testid="login-instructions-link"
                        fontSize={failedLogIn ? "1.125rem" : "0.875rem"}
                        secondary
                    >
                        Having trouble logging in?
                    </A>
                </LoginH3>
                <Support />
                <LiveChat />
            </AuthBlock>
        </AuthContentHolder>
    );
}

const LoginH3 = styled(H3)`
    margin: 0 auto 1rem auto;
    cursor: pointer;
`;

const WelcomeText = styled.div`
    margin: 1em;
`;

Login.propTypes = {
    clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    adviserId: PropTypes.string,
    location: PropTypes.object,
    authenticate: PropTypes.func,
    theme: PropTypes.shape({
        selectedTheme: PropTypes.object,
        isThemeReady: PropTypes.bool
    }),
    registrationUser: PropTypes.string,
    termsAccepted: PropTypes.bool,
    isLoading: PropTypes.bool,
    failedLogIn: PropTypes.bool,
    isPrintMode: PropTypes.bool
};
